import { useCallback, useEffect, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  type Location,
  type NavigateOptions,
} from "react-router-dom";
import {
  parseJourneyPointer,
  journeyAtTarget,
  safeJourneyTarget,
  safeQuoteOrigin,
  canonicalQuoteOrigin,
  stripJourneyState,
  type JourneyPage,
  type JourneyPageKey,
  type JourneySnapshot,
} from "./quoteJourneyState";
import {
  JOURNEY_POINTER_KEY,
  stageQuoteJourney,
  restoreQuoteJourney,
  type JourneyRepository,
  type JourneyStorage,
} from "./quoteJourneyPersistence";

import { beginQuoteJourneyOperation } from "./quoteJourneySession";

export type JourneyControllerOptions = {
  store: JourneyRepository;
  storage: JourneyStorage;
  now?: () => number;
  beforeNavigate?: (target: string) => Promise<boolean>;
  beforeRouteCommit?: (target: string) => void;
  validateSnapshot?: (snapshot: JourneySnapshot) => boolean;
};
export type JourneyController = {
  background: Location | null;
  restoring: boolean;
  error: string | null;
  busy: boolean;
  originHref: string | null;
  /** 当前活动暂存的 id；后退/前进到带同一 id 的设置页时原页停放、不卸载（W6 据此不问会丢的文件）。 */
  journeyId: string | null;
  restoredPage: (key: JourneyPageKey) => JourneyPage | null;
  registerPage: (key: JourneyPageKey, capture: () => JourneyPage) => () => void;
  openSettings: (target: string) => Promise<boolean>;
  /**
   * 原页停放期间又改了只能随暂存保留的内容（如后退回原页后才传的文件）：
   * 前进回到当前暂存的设置页之前，把原页重新写进同一份暂存（id 不变），刷新/关页后恢复的是最新内容。
   */
  refreshStash: () => Promise<boolean>;
  returnToOrigin: () => Promise<boolean>;
  clearCurrentWork: () => Promise<boolean>;
};
type LiveJourney = { snapshot: JourneySnapshot; originLocation: Location };
const hrefOf = (location: Location) =>
  location.pathname + location.search + location.hash;
function restoredLocation(snapshot: JourneySnapshot): Location {
  const url = new URL(snapshot.origin, "https://quote.invalid");
  return {
    pathname: url.pathname,
    search: url.search,
    hash: url.hash,
    state: { quoteJourneyId: snapshot.id },
    key: `quote-origin-${snapshot.id}`,
  };
}

/** 调度边界；调用方按身份key隔离，并为背景/当前路由提供不同的可交互上下文。 */
export function useQuoteJourneyController(
  scope: string,
  options: JourneyControllerOptions,
): JourneyController {
  const location = useLocation(),
    routerNavigate = useNavigate();
  const current = useRef({ scope, location, options });
  current.current = { scope, location, options };
  const navigate = useCallback(
    (target: string, settings?: NavigateOptions) => {
      current.current.options.beforeRouteCommit?.(target);
      return routerNavigate(target, settings);
    },
    [routerNavigate],
  );
  const mounted = useRef(true),
    operation = useRef(0),
    busyRef = useRef(false);
  const captures = useRef(
    new Map<JourneyPageKey, { capture: () => JourneyPage }>(),
  );
  const [journey, setJourney] = useState<LiveJourney | null>(null);
  const enteredJourney = useRef<string | null>(null);
  const liveJourney = useRef(journey);
  liveJourney.current = journey;
  const [restored, setRestored] = useState<JourneySnapshot | null>(null);
  const pendingEntry = useRef<{ id: string; fromKey: string } | null>(null);
  const clearingEntry = useRef<{ fromKey: string } | null>(null);
  const [settledRestore, setSettledRestore] = useState<string | null>(null);
  const routeId = location.state?.quoteJourneyId;
  const allowed =
    safeJourneyTarget(hrefOf(location)) || safeQuoteOrigin(hrefOf(location));
  const resumeId =
    typeof routeId === "string" && allowed
      ? parseJourneyPointer(
          JSON.stringify({ version: 1, scope, id: routeId }),
          scope,
        )
      : null;
  const requestKey = JSON.stringify([
    scope,
    location.key,
    hrefOf(location),
    resumeId,
  ]);
  const entering = pendingEntry.current?.fromKey === location.key;
  const restoring = Boolean(
    resumeId &&
    journey?.snapshot.id !== resumeId &&
    settledRestore !== requestKey &&
    !entering &&
    clearingEntry.current?.fromKey !== location.key,
  );
  const [error, setError] = useState<string | null>(null),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    setSettledRestore(null);
    if (clearingEntry.current?.fromKey !== location.key)
      clearingEntry.current = null;
  }, [location.key]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      operation.current += 1;
    };
  }, []);
  useEffect(() => {
    if (!restoring || !resumeId) return;
    let alive = true;
    const start = current.current;
    const matchesStart = () =>
      alive &&
      current.current.location.key === start.location.key &&
      current.current.scope === start.scope;
    restoreQuoteJourney(resumeId, start.scope, {
      store: start.options.store,
      storage: start.options.storage,
      now: start.options.now?.(),
    })
      .then((snapshot) => {
        if (!matchesStart()) return;
        if (
          !snapshot ||
          (start.options.validateSnapshot &&
            !start.options.validateSnapshot(snapshot)) ||
          (safeQuoteOrigin(hrefOf(start.location)) &&
            canonicalQuoteOrigin(hrefOf(start.location)) !==
              canonicalQuoteOrigin(snapshot.origin) &&
            !journeyAtTarget(hrefOf(start.location), snapshot.origin))
        ) {
          setError("原报价暂存已失效，请从报价页重新打开设置");
          setSettledRestore(requestKey);
        } else {
          setRestored(snapshot);
          setJourney({ snapshot, originLocation: restoredLocation(snapshot) });
          setError(null);
          setSettledRestore(null);
        }
      })
      .catch(() => {
        if (!matchesStart()) return;
        setError("暂时没能恢复原报价，请留在这里重试");
        setSettledRestore(requestKey);
      });
    return () => {
      alive = false;
    };
  }, [restoring, resumeId, requestKey]);
  // 来源实例在同次前进/回退期间停放；历史请求另一快照时交给恢复流程。
  useEffect(() => {
    if (!journey) return;
    if (
      journeyAtTarget(hrefOf(location), journey.snapshot.origin) &&
      resumeId === journey.snapshot.id
    ) {
      enteredJourney.current = journey.snapshot.id;
      pendingEntry.current = null;
      return;
    }
    if (
      canonicalQuoteOrigin(hrefOf(location)) ===
      canonicalQuoteOrigin(journey.snapshot.origin)
    ) {
      if (entering) return;
      if (resumeId && resumeId !== journey.snapshot.id) {
        setJourney(null);
        setRestored(null);
        return;
      }
      if (!resumeId && enteredJourney.current === journey.snapshot.id)
        navigate(journey.snapshot.origin, {
          replace: true,
          state: { quoteJourneyId: journey.snapshot.id },
        });
      return;
    }
    setJourney(null);
    setRestored(null);
    enteredJourney.current = null;
    pendingEntry.current = null;
  }, [journey, location, navigate, resumeId, entering]);
  const registerPage = useCallback(
    (key: JourneyPageKey, capture: () => JourneyPage) => {
      const entry = { capture };
      captures.current.set(key, entry);
      return () => {
        if (captures.current.get(key) === entry) captures.current.delete(key);
      };
    },
    [],
  );
  /** 收集各页当前内容；还没登记或收集失败返回 null。 */
  const capturePages = useCallback((): JourneyPage[] | null => {
    if (!captures.current.size) return null;
    try {
      return [...captures.current.entries()].map(([key, entry]) => {
        const page = entry.capture();
        if (page.key !== key) throw new Error();
        return page;
      });
    } catch {
      return null;
    }
  }, []);
  const isCurrent = useCallback(
    (token: number, start: typeof current.current) =>
      mounted.current &&
      operation.current === token &&
      current.current.scope === start.scope &&
      current.current.location.key === start.location.key,
    [],
  );
  const openSettings = useCallback(
    async (target: string): Promise<boolean> => {
      if (!safeJourneyTarget(target) || busyRef.current) return false;
      const start = current.current,
        token = ++operation.current;
      const finishOperation = beginQuoteJourneyOperation(start.scope);
      busyRef.current = true;
      setBusy(true);
      setError(null);
      try {
        if (
          start.options.beforeNavigate &&
          !(await start.options.beforeNavigate(target))
        )
          return false;
        if (!isCurrent(token, start)) return false;
        const existing = liveJourney.current;
        if (
          existing &&
          start.location.state?.quoteJourneyId === existing.snapshot.id &&
          journeyAtTarget(hrefOf(start.location), existing.snapshot.origin)
        ) {
          navigate(target, { state: { quoteJourneyId: existing.snapshot.id } });
          return true;
        }
        const origin = safeQuoteOrigin(hrefOf(start.location));
        if (!origin) {
          navigate(target);
          return true;
        }
        const pages = capturePages();
        if (!pages) {
          setError("当前报价还没准备好，请稍后再打开设置");
          return false;
        }
        const snapshot: JourneySnapshot = {
          version: 1,
          id: crypto.randomUUID(),
          scope: start.scope,
          createdAt: start.options.now?.() ?? Date.now(),
          origin,
          target,
          pages,
        };
        if (
          start.options.validateSnapshot &&
          !start.options.validateSnapshot(snapshot)
        ) {
          setError("当前报价暂存信息不完整，请留在这里重试");
          return false;
        }
        // 在文件事务与指针之间再核对位置/身份，离开后迟到不写新指针。
        const store: JourneyRepository = {
          ...start.options.store,
          save: async (value) => {
            await start.options.store.save(value);
            if (!isCurrent(token, start)) {
              await start.options.store.delete(value.id);
              throw new Error("cancelled");
            }
          },
        };
        try {
          await stageQuoteJourney(snapshot, {
            store,
            storage: start.options.storage,
            now: start.options.now?.(),
          });
        } catch (failure) {
          if (isCurrent(token, start))
            setError(
              failure instanceof Error
                ? failure.message
                : "暂时没能保存返回位置，请留在这里重试",
            );
          return false;
        }
        if (!isCurrent(token, start)) return false;
        pendingEntry.current = { id: snapshot.id, fromKey: start.location.key };
        setJourney({ snapshot, originLocation: start.location });
        navigate(target, { state: { quoteJourneyId: snapshot.id } });
        return true;
      } catch {
        if (isCurrent(token, start))
          setError("暂时没能打开设置，请留在这里重试");
        return false;
      } finally {
        busyRef.current = false;
        if (mounted.current && operation.current === token) setBusy(false);
        finishOperation();
      }
    },
    [navigate, isCurrent, capturePages],
  );
  const refreshStash = useCallback(async (): Promise<boolean> => {
    const live = liveJourney.current;
    if (!live || busyRef.current) return false;
    const start = current.current,
      token = ++operation.current,
      finishOperation = beginQuoteJourneyOperation(start.scope);
    busyRef.current = true;
    setBusy(true);
    setError(null);
    const failed = "暂时没能把刚传的内容存进原报价暂存，请留在这里重试";
    try {
      const pages = capturePages();
      const snapshot: JourneySnapshot | null = pages
        ? { ...live.snapshot, pages }
        : null;
      if (
        !snapshot ||
        (start.options.validateSnapshot &&
          !start.options.validateSnapshot(snapshot))
      ) {
        setError(failed);
        return false;
      }
      // 同一 id 覆盖写：设置页那条历史项带的是这个 id，换 id 回不去
      await start.options.store.save(snapshot);
      return isCurrent(token, start);
    } catch {
      if (isCurrent(token, start)) setError(failed);
      return false;
    } finally {
      busyRef.current = false;
      if (mounted.current && operation.current === token) setBusy(false);
      finishOperation();
    }
  }, [isCurrent, capturePages]);
  const clearCurrentWork = useCallback(async (): Promise<boolean> => {
    const origin =
      safeQuoteOrigin(hrefOf(current.current.location)) ??
      safeQuoteOrigin(current.current.location.pathname);
    if (busyRef.current || !origin) return false;
    const start = current.current,
      token = ++operation.current,
      finishOperation = beginQuoteJourneyOperation(start.scope);
    busyRef.current = true;
    setBusy(true);
    setError(null);
    let removed: string | null = null;
    try {
      if (
        start.options.beforeNavigate &&
        !(await start.options.beforeNavigate(origin))
      )
        return false;
      if (!isCurrent(token, start)) return false;
      let cacheUnavailable = false;
      try {
        const raw = start.options.storage.getItem(JOURNEY_POINTER_KEY),
          id = parseJourneyPointer(raw, start.scope);
        if (id) {
          start.options.storage.removeItem(JOURNEY_POINTER_KEY);
          removed = raw;
          await start.options.store.delete(id, start.scope);
        }
      } catch (error) {
        if (liveJourney.current) throw error;
        // 没有当前快照时，暂存故障不能封住首次报价；不宣称旧缓存已清理。
        cacheUnavailable = true;
      }
      if (!isCurrent(token, start)) return false;
      clearingEntry.current = { fromKey: start.location.key };
      navigate(origin, {
        replace: true,
        state: stripJourneyState(start.location.state),
      });
      pendingEntry.current = null;
      enteredJourney.current = null;
      setJourney(null);
      setRestored(null);
      setSettledRestore(null);
      if (cacheUnavailable) setError("浏览器暂存暂不可用，新报价仍可继续");
      return true;
    } catch {
      if (isCurrent(token, start)) {
        let restored = true;
        if (removed) {
          try {
            if (start.options.storage.getItem(JOURNEY_POINTER_KEY) === null)
              start.options.storage.setItem(JOURNEY_POINTER_KEY, removed);
          } catch {
            restored = false;
          }
        }
        setError(
          restored
            ? "暂存暂时没能清理，请留在这里重试"
            : "暂存状态未能恢复，请留在这里重试",
        );
      }
      return false;
    } finally {
      busyRef.current = false;
      if (mounted.current && operation.current === token) setBusy(false);
      finishOperation();
    }
  }, [isCurrent, navigate]);

  const returnToOrigin = useCallback(async (): Promise<boolean> => {
    if (busyRef.current) return false;
    const start = current.current,
      token = ++operation.current;
    const origin = liveJourney.current?.snapshot.origin ?? "/app/quickquote";
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      if (
        start.options.beforeNavigate &&
        !(await start.options.beforeNavigate(origin))
      )
        return false;
      if (!isCurrent(token, start)) return false;
      const prior = liveJourney.current;
      navigate(origin, {
        replace: true,
        state: prior ? { quoteJourneyId: prior.snapshot.id } : null,
      });
      return true;
    } catch {
      if (isCurrent(token, start))
        setError("暂时没能返回原报价，请留在这里重试");
      return false;
    } finally {
      busyRef.current = false;
      if (mounted.current && operation.current === token) setBusy(false);
    }
  }, [navigate, isCurrent]);
  return {
    background:
      journey &&
      resumeId === journey.snapshot.id &&
      journeyAtTarget(hrefOf(location), journey.snapshot.origin)
        ? journey.originLocation
        : null,
    restoring,
    error,
    busy,
    originHref: journey?.snapshot.origin ?? null,
    journeyId: journey?.snapshot.id ?? null,
    registerPage,
    restoredPage: (key) =>
      restored?.pages.find((page) => page.key === key) ?? null,
    openSettings,
    refreshStash,
    returnToOrigin,
    clearCurrentWork,
  };
}
